import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import {
  Redirect,
  withRouter,
} from 'react-router-dom';
import { useIntl } from 'react-intl';

import { clearUserProfile } from '../actions/Auth';
import {
  fetchUploadPage,
  setUploadPageLoading,
} from '../actions/UploadPages';

import UploadDropOverlay from '../components/UploadDropOverlay';
import UploadPageFiles from '../components/UploadPage/Files';
import UploadPageHeader from '../components/UploadPage/Header';
import UploadPageTitle from '../components/UploadPage/Title';
import UploadPageUnlockAccess from '../components/UploadPage/UnlockAccess';
import UploadPageUploader from '../components/UploadPage/Uploader';
import FlashMessages from '../components/FlashMessages';
import UploadPageFooter from '../components/UploadPage/Footer';

import Loading from '../components/Loading';
import LoadingMain from '../components/LoadingMain';
import NothingFound from '../components/NothingFound';
import TermsModalHandler from '../components/TermsModalHandler';

import { hideIntercomWidget } from '../helpers';
import {
  cleanLoginParams,
  accountUsageLock,
  subscriptionExpired,
} from '../helpers/App';
import { uploadFilesTarget } from '../helpers/DNDUploadFiles';
import withDragAndDropContext from '../helpers/withDragAndDropContext';

const UploadPage = ({
  connectDropTarget,
  isOver,
  uploadPage,
  uploadPageLoading,
  uploadPageLocked,
  uploadPageDragDropEnabled,
  uploadPageNotFound,
  uploadPageError,
  removeUserProfileFromState,
  location,
  match,
  profile,
  loadUploadPage,
}) => {
  const intl = useIntl();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [storageLimitExceeded, setStorageLimitExceeded] = useState(false);

  const {
    uploadPageId,
    uploadUuid,
  } = match.params;

  const switchSelectFile = (id) => {
    if (selectedFiles.includes(id)) {
      setSelectedFiles(selectedFiles.filter(sf => (sf !== id)));
    } else {
      setSelectedFiles(prevSelectedFiles => ([...prevSelectedFiles, id]));
    }
  };

  useEffect(() => {
    hideIntercomWidget();
  }, []);

  useEffect(() => {
    if (uploadPageId) {
      loadUploadPage(uploadPageId);
    }
  }, [uploadPageId, loadUploadPage]);

  const checkUploadLimit = useCallback(() => {
    if (!profile) {
      return true;
    }

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      setStorageLimitExceeded(true);
      return false;
    }
    setStorageLimitExceeded(false);
    return true;
  }, [profile]);

  useEffect(() => {
    checkUploadLimit();
  }, [checkUploadLimit]);

  const resetUploadPage = () => {
    checkUploadLimit();
  };

  const redirect = profile && !profile.uuid &&
    uploadPage && uploadPage.require_login;

  if (redirect) {
    removeUserProfileFromState();
    return (
      <Redirect
        to={{
          pathname: '/login',
          search: location.search,
          state: {
            from: `${location.pathname}${cleanLoginParams(location.search)}`,
          },
        }}
      />
    );
  }

  if (uploadPageLoading) {
    return (
      <div className="layout layout-upload">
        <LoadingMain />
      </div>
    );
  }

  if (uploadPageNotFound) {
    return (
      <div className="layout layout-upload">
        <UploadPageHeader
          notFound={uploadPageNotFound}
        />
        <NothingFound text={intl.formatMessage({ defaultMessage: 'Guest upload page not found' })} />
      </div>
    );
  }

  if (uploadPageError) {
    return (
      <div className="layout layout-upload">
        <UploadPageHeader
          notFound={true}
        />
        <p className="center">
          {uploadPageError}
        </p>
      </div>
    );
  }

  if (!uploadPage?.active) {
    return (
      <div className="layout layout-upload">
        <UploadPageHeader
          notFound={true}
        />
        <NothingFound text={intl.formatMessage({ defaultMessage: 'This guest upload page has expired' })} />
      </div>
    );
  }

  if (uploadPageLocked) {
    return (
      <div className="layout layout-upload">
        <TermsModalHandler uploadPage={uploadPage} />
        <UploadPageUnlockAccess />
      </div>
    );
  }

  return connectDropTarget((
    <div className="layout layout-upload">
      {uploadPageDragDropEnabled && (
        <UploadDropOverlay show={isOver} />
      )}
      <div className="upload-main">
        <UploadPageHeader
          notFound={uploadPageNotFound}
        />
        <div className="upload-content">
          <UploadPageTitle />
          {(uploadUuid && profile?.client) ? (
            <UploadPageFiles
              uploadUuid={uploadUuid}
            />
          ) : (
            <>
              {(profile?.client && !storageLimitExceeded) ? (
                <UploadPageUploader
                  profile={profile}
                  selectedFiles={selectedFiles}
                  switchSelectFile={switchSelectFile}
                  setSelectedFiles={setSelectedFiles}
                  checkUploadLimit={checkUploadLimit}
                  storageLimitExceeded={storageLimitExceeded}
                  resetUploadPage={resetUploadPage}
                />
              ) : (
                <Loading />
              )}
              {storageLimitExceeded && (
                <div className="center">
                  <h5 className="error">
                    {intl.formatMessage({ defaultMessage: 'The storage limit for this account has been exceeded' })}
                  </h5>
                  <p>{intl.formatMessage({ defaultMessage: 'Contact the workspace administrator' })}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <FlashMessages />
      <UploadPageFooter />
      <TermsModalHandler uploadPage={uploadPage} />
    </div>
  ));
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  uploadPage: state.UploadPages.uploadPage,
  uploadPageLoading: state.UploadPages.loading,
  uploadPageLocked: state.UploadPages.locked,
  uploadPageDragDropEnabled: state.UploadPages.dragDropEnabled,
  uploadPageNotFound: state.UploadPages.notFound,
  uploadPageError: state.UploadPages.notFound,
});

const mapDispatchToProps = dispatch => ({
  setLoading: (loading) => {
    dispatch(setUploadPageLoading(loading));
  },
  loadUploadPage: (uploadPageUuid) => {
    dispatch(fetchUploadPage(uploadPageUuid));
  },
  removeUserProfileFromState: () => {
    dispatch(clearUserProfile());
  },
});

UploadPage.propTypes = {
  connectDropTarget: PropTypes.func.isRequired,
  isOver: PropTypes.bool.isRequired,
  uploadPage: PropTypes.object,
  uploadPageLoading: PropTypes.bool.isRequired,
  uploadPageLocked: PropTypes.bool.isRequired,
  uploadPageDragDropEnabled: PropTypes.bool.isRequired,
  uploadPageNotFound: PropTypes.bool.isRequired,
  uploadPageError: PropTypes.bool.isRequired,
  removeUserProfileFromState: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  loadUploadPage: PropTypes.func.isRequired,
};

UploadPage.defaultProps = {
  profile: null,
  uploadPage: null,
};

export default withDragAndDropContext(
  flow(DropTarget(NativeTypes.FILE, uploadFilesTarget, (DNDconnect, monitor) => ({
    connectDropTarget: DNDconnect.dropTarget(),
    isOver: monitor.isOver({ shallow: false }),
  })), connect())(
    withRouter(connect(mapStateToProps, mapDispatchToProps)(UploadPage)),
  ),
);
